/**
 * Platform-admin auth store.
 *
 * The shared `createAuthStore` factory from `@edupra/shell` gives us the
 * zustand-backed token + user persistence (tokenKey = `edupra_platform_token`,
 * a *separate* localStorage slot from the tenant app so the two realms can
 * coexist on the same browser without colliding).
 *
 * Login + 2FA + `me` are NOT REST endpoints on the platform side — they are
 * tRPC mutations/queries (`auth.login`, `auth.verifyTotp`, `auth.enrollTotp`,
 * `auth.me`). The login page calls those via the tRPC client directly and
 * then writes the resulting token + user back to this store via
 * `useAuthStore.getState().setAuth(token, user)`.
 *
 * We re-export `checkAuth` here as a tRPC-aware bootstrap that uses the
 * platform-tRPC client to hit `auth.me` (the shell-default `checkAuth` hits
 * REST `/me` and would 404 on the platform surface).
 */

import { createAuthStore, type AuthUser } from '@edupra/shell';
import { platformTRPCProxy } from './platform-trpc';

export type PlatformRole = 'owner' | 'admin' | 'support' | 'billing' | 'viewer';

export interface PlatformUser extends AuthUser {
  role: PlatformRole;
}

const auth = createAuthStore<PlatformUser>({
  tokenKey: 'edupra_platform_token',
  apiBase: '/trpc/platform',
  logoutRedirect: '/login',
});

export const useAuthStore = auth.useAuthStore;
export const useAuth = auth.useAuth;
export const getToken = auth.getToken;
export const logout = auth.logout;

/**
 * Validate the currently-stored token against the platform tRPC `auth.me`
 * query. On success, refresh the user object in the store. On any failure
 * (expired, revoked, network error), clear the store so the router shows
 * the login screen.
 */
export async function checkAuth(): Promise<void> {
  if (typeof window === 'undefined') {
    useAuthStore.getState().clearAuth();
    return;
  }

  const token = localStorage.getItem('edupra_platform_token');
  if (!token) {
    useAuthStore.getState().clearAuth();
    return;
  }

  try {
    const me = await platformTRPCProxy.auth.me.query();
    useAuthStore.getState().setAuth(token, {
      id: me.id,
      email: me.email,
      name: me.name,
      role: me.role as PlatformRole,
    });
  } catch {
    useAuthStore.getState().clearAuth();
  }
}
